import { QueryClient } from '@tanstack/react-query';

// Create a client with optimized settings for time tracking
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time entries data is relatively stable, so we can cache for a reasonable time
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Refetch when network reconnects
    },
    mutations: {
      retry: 1, // Retry mutations once on failure
    },
  },
});

// Query keys for consistent cache management
export const queryKeys = {
  timeEntries: {
    all: ['timeEntries'] as const,
    lists: () => [...queryKeys.timeEntries.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.timeEntries.lists(), filters] as const,
    details: () => [...queryKeys.timeEntries.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.timeEntries.details(), id] as const,
    stats: (filters: Record<string, any>) => [...queryKeys.timeEntries.all, 'stats', filters] as const,
  },
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.projects.lists(), filters] as const,
  },
  retainers: {
    all: ['retainers'] as const,
    lists: () => [...queryKeys.retainers.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.retainers.lists(), filters] as const,
  },
  podAssignments: {
    all: ['podAssignments'] as const,
    lists: () => [...queryKeys.podAssignments.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.podAssignments.lists(), filters] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },
  permissions: {
    all: ['permissions'] as const,
    userPermissions: (userId: string) => [...queryKeys.permissions.all, 'user', userId] as const,
    rolePermissions: (role: string) => [...queryKeys.permissions.all, 'role', role] as const,
    availablePermissions: () => [...queryKeys.permissions.all, 'available'] as const,
    cacheMetrics: () => [...queryKeys.permissions.all, 'cache', 'metrics'] as const,
  },
  holidays: {
    all: ['holidays'] as const,
    lists: () => [...queryKeys.holidays.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.holidays.lists(), filters] as const,
    myRequests: () => [...queryKeys.holidays.all, 'my-requests'] as const,
    pendingApprovals: () => [...queryKeys.holidays.all, 'pending-approvals'] as const,
    calendar: (params: Record<string, any>) => [...queryKeys.holidays.all, 'calendar', params] as const,
    details: () => [...queryKeys.holidays.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.holidays.details(), id] as const,
    approvalContext: (id: string, params: Record<string, any>) =>
      [...queryKeys.holidays.all, 'approval-context', id, params] as const,
    /** The whole approval-rules management overview (docs/holiday-approval-rules.md, Task 7). */
    approvalRules: () => [...queryKeys.holidays.all, 'approval-rules'] as const,
  },
  holidayAllowances: {
    all: ['holidayAllowances'] as const,
    lists: () => [...queryKeys.holidayAllowances.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.holidayAllowances.lists(), filters] as const,
    myAllowance: (year?: number) => [...queryKeys.holidayAllowances.all, 'my-allowance', year] as const,
    teamSummary: () => [...queryKeys.holidayAllowances.all, 'team-summary'] as const,
    podSummary: (podId: string) => [...queryKeys.holidayAllowances.all, 'pod-summary', podId] as const,
    details: () => [...queryKeys.holidayAllowances.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.holidayAllowances.details(), id] as const,
  },
} as const;
